/** Query keys (prefix ["ws", wsId] = everything persisted for a workspace) and cache update helpers. */
import { Messages, Outbox, type MessagePage, type PendingEntry, type WorkspaceCache } from "@bundle/core";
import type { InfiniteData, QueryClient } from "@tanstack/query-core";

export type MessagesData = InfiniteData<MessagePage, number | null>;

export const keys = {
  all: (ws: string) => ["ws", ws] as const,
  workspace: (ws: string) => ["ws", ws, "bootstrap"] as const,
  messagesAll: (ws: string) => ["ws", ws, "messages"] as const,
  messages: (ws: string, channelId: string) => ["ws", ws, "messages", channelId] as const,
  threadsAll: (ws: string) => ["ws", ws, "thread"] as const,
  thread: (ws: string, rootId: string) => ["ws", ws, "thread", rootId] as const,
  outbox: (ws: string) => ["ws", ws, "outbox"] as const,
};

export function updateWorkspace(client: QueryClient, ws: string, f: (c: WorkspaceCache) => WorkspaceCache): void {
  client.setQueryData<WorkspaceCache>(keys.workspace(ws), (c) => (c === undefined ? c : f(c)));
}

export function updatePages(client: QueryClient, ws: string, channelId: string, f: (pages: MessagePage[]) => MessagePage[]): void {
  client.setQueryData<MessagesData>(keys.messages(ws, channelId), (d) => {
    if (d === undefined) return d;
    const pages = f(d.pages);
    return pages === d.pages ? d : { ...d, pages };
  });
}

/** Applies `f` to every cached thread of the workspace (no-op reducers leave others untouched). */
export function updateThreads(client: QueryClient, ws: string, f: (page: MessagePage, rootId: string) => MessagePage): void {
  for (const q of client.getQueryCache().findAll({ queryKey: keys.threadsAll(ws) })) {
    const rootId = q.queryKey[3];
    if (typeof rootId !== "string") continue;
    client.setQueryData<MessagePage>(q.queryKey, (p) => (p === undefined ? p : f(p, rootId)));
  }
}

const EMPTY: PendingEntry[] = [];

export function getOutbox(client: QueryClient, ws: string): PendingEntry[] {
  return client.getQueryData<PendingEntry[]>(keys.outbox(ws)) ?? EMPTY;
}

export function updateOutbox(client: QueryClient, ws: string, f: (o: PendingEntry[]) => PendingEntry[]): void {
  const next = f(getOutbox(client, ws));
  if (next !== getOutbox(client, ws)) client.setQueryData<PendingEntry[]>(keys.outbox(ws), next);
}

export { Messages, Outbox };
